import { useState, useEffect } from 'react';
import axios from '../../configApi/axiosConfig';
import { APIURL } from "../../configApi/apiConfig";
import { useSnackbar } from 'notistack';
import { useAuth } from "../AuthContext/AuthContext";

export function ClientesBuenosMotos() {
    const [cedulas, setCedulas] = useState([]);
    const [filtro, setFiltro] = useState('');
    const [nuevaCedula, setNuevaCedula] = useState('');
    const [nuevaDescripcion, setNuevaDescripcion] = useState('');
    const [mostrarFormulario, setMostrarFormulario] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const { userData } = useAuth();

    // Estados para paginación
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Modal de revocación (pide motivo obligatorio al quitarle lo "bueno")
    const [cedulaARevocar, setCedulaARevocar] = useState(null);
    const [motivoRevocacion, setMotivoRevocacion] = useState('');

    useEffect(() => {
        fetchCedulas();
    }, []);

    const fetchCedulas = async () => {
        try {
            setLoading(true);
            const response = await axios.get(APIURL.getClientesBuenosMotos());
            const cedulasMapeadas = response.data.map(item => ({
                id: item.idCbo_ClientesBuenosMotos,
                cedula: item.Cedula,
                descripcion: item.Observacion,
                activo: item.Activo,
                Usuario: item.Usuario,
                FechaSistema: item.FechaSistema,
            }));
            setCedulas(cedulasMapeadas);
            setError(null);
        } catch (err) {
            console.error("Error al cargar cédulas:", err);
            setError("No se pudieron cargar las cédulas. Intente de nuevo más tarde.");
        } finally {
            setLoading(false);
        }
    };

    const cedulasFiltradas = cedulas.filter(item =>
        item.cedula?.includes(filtro) ||
        item.descripcion?.toLowerCase().includes(filtro.toLowerCase())
    );

    // Lógica de paginación
    const totalPages = Math.ceil(cedulasFiltradas.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const cedulasPaginadas = cedulasFiltradas.slice(startIndex, endIndex);

    useEffect(() => {
        setCurrentPage(1);
    }, [filtro]);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };

    // Reactivar (volver a habilitar para Motos) no pide motivo.
    // Revocar (quitarle lo "bueno") sí lo pide, porque manda al cliente a Yupana.
    const toggleActivo = (cedula) => {
        if (cedula.activo) {
            setCedulaARevocar(cedula);
            setMotivoRevocacion('');
            return;
        }
        aplicarCambioEstado(cedula.id, true);
    };

    const aplicarCambioEstado = async (id, nuevoEstado, observacion) => {
        try {
            await axios.patch(APIURL.updateClienteBuenoMotos(id), {
                activo: nuevoEstado,
                ...(observacion ? { observacion } : {}),
            });
            setCedulas(cedulas.map(c =>
                c.id === id ? { ...c, activo: nuevoEstado, ...(observacion ? { descripcion: observacion } : {}) } : c
            ));
            enqueueSnackbar(
                nuevoEstado ? "Cédula habilitada nuevamente para Motos." : "Cédula removida — ahora irá por Yupana.",
                { variant: 'success' }
            );
        } catch (err) {
            console.error("Error al actualizar estado:", err);
            enqueueSnackbar("No se pudo actualizar el estado de la cédula.", { variant: 'error' });
        }
    };

    const confirmarRevocacion = () => {
        const motivo = motivoRevocacion.trim();
        if (!motivo) {
            enqueueSnackbar("El motivo es obligatorio para remover una cédula.", { variant: 'warning' });
            return;
        }
        aplicarCambioEstado(cedulaARevocar.id, false, motivo);
        setCedulaARevocar(null);
        setMotivoRevocacion('');
    };

    const agregarCedula = async () => {
        const cedula = nuevaCedula.trim();
        const descripcion = nuevaDescripcion.trim();

        if (!cedula) {
            enqueueSnackbar("La cédula es obligatoria.", { variant: 'warning' });
            return;
        }

        if (!/^[0-9]+$/.test(cedula)) {
            enqueueSnackbar("La cédula solo debe contener dígitos numéricos.", { variant: 'warning' });
            return;
        }

        if (cedula.length !== 10) {
            enqueueSnackbar("La cédula debe tener exactamente 10 dígitos.", { variant: 'warning' });
            return;
        }

        if (!descripcion) {
            enqueueSnackbar("La observación es obligatoria.", { variant: 'warning' });
            return;
        }

        try {
            await axios.post(APIURL.postClienteBuenoMotos(), {
                Cedula: cedula,
                Observacion: descripcion,
                Activo: true,
                Usuario: userData.Nombre,
            });

            await fetchCedulas();
            setNuevaCedula('');
            setNuevaDescripcion('');
            setMostrarFormulario(false);

            enqueueSnackbar("Cédula agregada exitosamente.", { variant: 'success' });

        } catch (err) {
            console.error("❌ Error al agregar cédula:", err.response?.data || err);
            const mensaje = err.response?.data?.message || "No se pudo agregar la cédula.";
            enqueueSnackbar(mensaje, { variant: 'error' });
        }
    };

    const cancelarFormulario = () => {
        setNuevaCedula('');
        setNuevaDescripcion('');
        setMostrarFormulario(false);
    };

    // SVG Icons
    const AddIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
    );

    const IdIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V4a2 2 0 114 0v2m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
        </svg>
    );

    const SearchIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
    );

    const CheckCircleIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
    );

    const YupanaIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
        </svg>
    );

    const BikeIcon = ({ className = "h-6 w-6" }) => (
        <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <circle cx="5.5" cy="17.5" r="3.5" strokeWidth={2} />
            <circle cx="18.5" cy="17.5" r="3.5" strokeWidth={2} />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 17.5V14l-3-3 4-3 2 3h3" />
        </svg>
    );

    const ChevronLeftIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
    );

    const ChevronRightIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
    );

    return (
        <div className="bg-gray-900 min-h-screen p-6">
            <div className="max-w-6xl mx-auto bg-gray-800 rounded-lg shadow-2xl p-6 border-t-4 border-emerald-600">
                <div className="flex justify-between items-center mb-8 border-b border-gray-700 pb-4">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-emerald-600 rounded-full">
                            <BikeIcon />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-white">Clientes Buenos — Motos</h1>
                            <p className="text-sm text-gray-400 mt-1">Cédulas que calificaron como recurrentes para Motos. Un administrador puede removerlas para que vayan por Yupana.</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setMostrarFormulario(!mostrarFormulario)}
                        className="flex items-center bg-emerald-600 hover:bg-emerald-700 text-white py-2 px-4 rounded-md transition-all duration-200 transform hover:scale-105"
                    >
                        <AddIcon />
                        <span className="ml-2">Agregar Cédula</span>
                    </button>
                </div>

                {mostrarFormulario && (
                    <div className="mb-8 p-6 border rounded-md bg-gray-700 border-emerald-500 shadow-md animate-fadeIn">
                        <h2 className="text-xl font-semibold mb-4 text-white flex items-center">
                            <IdIcon />
                            <span className="ml-2">Nueva Cédula</span>
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Número de Cédula</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <IdIcon />
                                    </div>
                                    <input
                                        type="text"
                                        value={nuevaCedula}
                                        onChange={(e) => setNuevaCedula(e.target.value)}
                                        className="w-full p-3 pl-10 bg-gray-600 border border-gray-500 rounded-md text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                        placeholder="10 dígitos"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Observación</label>
                                <input
                                    type="text"
                                    value={nuevaDescripcion}
                                    onChange={(e) => setNuevaDescripcion(e.target.value)}
                                    className="w-full p-3 bg-gray-600 border border-gray-500 rounded-md text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                    placeholder="Ej: agregada manualmente por el administrador"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={cancelarFormulario}
                                className="py-2 px-4 border border-gray-500 rounded-md text-gray-300 hover:bg-gray-600 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={agregarCedula}
                                className="py-2 px-4 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors flex items-center"
                            >
                                <AddIcon />
                                <span className="ml-2">Agregar</span>
                            </button>
                        </div>
                    </div>
                )}

                <div className="mb-6 relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                        <SearchIcon />
                    </div>
                    <input
                        type="text"
                        placeholder="Buscar por cédula o observación..."
                        className="pl-12 p-3 w-full bg-gray-700 border border-gray-600 rounded-md text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        value={filtro}
                        onChange={(e) => setFiltro(e.target.value)}
                    />
                </div>

                {loading ? (
                    <div className="text-center py-16">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-solid border-emerald-600 border-t-transparent"></div>
                        <p className="mt-4 text-gray-300 text-lg">Cargando lista de cédulas...</p>
                    </div>
                ) : error ? (
                    <div className="text-center py-12 bg-gray-700 rounded-lg">
                        <div className="text-red-500 text-5xl mb-4">⚠️</div>
                        <p className="text-xl text-red-400">{error}</p>
                        <button
                            onClick={fetchCedulas}
                            className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white py-2 px-4 rounded-md"
                        >
                            Reintentar
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="mb-4 flex justify-between items-center text-gray-300 text-sm">
                            <span>
                                Mostrando {cedulasFiltradas.length === 0 ? 0 : startIndex + 1}-{Math.min(endIndex, cedulasFiltradas.length)} de {cedulasFiltradas.length} cédulas
                            </span>
                            <div className="flex items-center space-x-2">
                                <label className="text-gray-300">Mostrar:</label>
                                <select
                                    value={itemsPerPage}
                                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                                    className="bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white"
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                </select>
                                <span className="text-gray-300">por página</span>
                            </div>
                        </div>

                        <div className="overflow-hidden rounded-lg shadow border border-gray-700">
                            <table className="min-w-full divide-y divide-gray-700">
                                <thead className="bg-gray-700">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">#</th>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Cédula</th>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Observación</th>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Estado</th>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Usuario</th>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Fecha</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-gray-800 divide-y divide-gray-700">
                                    {cedulasPaginadas.length > 0 ? (
                                        cedulasPaginadas.map((cedula, index) => (
                                            <tr key={cedula.id} className="hover:bg-gray-700 transition-colors">
                                                <td className="px-6 py-4 text-sm font-medium text-gray-300">
                                                    {startIndex + index + 1}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-300">
                                                    <div className="flex items-center">
                                                        <span className={`inline-flex items-center justify-center h-8 w-8 rounded-full mr-3 ${cedula.activo ? 'bg-emerald-600' : 'bg-amber-600'}`}>
                                                            <IdIcon />
                                                        </span>
                                                        <span className="font-mono">{cedula.cedula}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-300 max-w-xs">{cedula.descripcion}</td>
                                                <td className="px-6 py-4 text-sm">
                                                    <button
                                                        onClick={() => toggleActivo(cedula)}
                                                        title={cedula.activo ? 'Remover — pasará a ir por Yupana' : 'Volver a habilitar para Motos'}
                                                        className={`inline-flex items-center px-3 py-1 rounded-full transition-colors ${cedula.activo
                                                                ? 'bg-emerald-900 text-emerald-200 hover:bg-emerald-800'
                                                                : 'bg-amber-900 text-amber-200 hover:bg-amber-800'
                                                            }`}
                                                    >
                                                        {cedula.activo ? (
                                                            <>
                                                                <CheckCircleIcon />
                                                                <span className="ml-2">Habilitado Motos</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <YupanaIcon />
                                                                <span className="ml-2">Va por Yupana</span>
                                                            </>
                                                        )}
                                                    </button>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-300">{cedula.Usuario}</td>
                                                <td className="px-6 py-4 text-sm text-gray-300">{cedula.FechaSistema ? new Date(cedula.FechaSistema).toLocaleDateString() : ''}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-12 text-center text-gray-400">
                                                <div className="flex flex-col items-center justify-center">
                                                    <BikeIcon className="h-10 w-10" />
                                                    <p className="mt-2 text-lg">No se encontraron cédulas registradas.</p>
                                                    <p className="text-sm text-gray-500">Se registran automáticamente cuando un cliente califica como recurrente para Motos.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className="mt-6 flex items-center justify-between">
                                <div className="text-sm text-gray-300">
                                    Página {currentPage} de {totalPages}
                                </div>

                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className="flex items-center px-3 py-2 text-sm font-medium text-gray-300 bg-gray-700 border border-gray-600 rounded-l-md hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronLeftIcon />
                                        Anterior
                                    </button>

                                    <div className="flex items-center space-x-1">
                                        {[...Array(totalPages)].map((_, index) => {
                                            const pageNumber = index + 1;
                                            const isCurrentPage = pageNumber === currentPage;

                                            if (
                                                pageNumber === 1 ||
                                                pageNumber === totalPages ||
                                                (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)
                                            ) {
                                                return (
                                                    <button
                                                        key={pageNumber}
                                                        onClick={() => handlePageChange(pageNumber)}
                                                        className={`px-3 py-2 text-sm font-medium border ${isCurrentPage
                                                                ? 'bg-emerald-600 text-white border-emerald-600'
                                                                : 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600'
                                                            }`}
                                                    >
                                                        {pageNumber}
                                                    </button>
                                                );
                                            } else if (
                                                pageNumber === currentPage - 3 ||
                                                pageNumber === currentPage + 3
                                            ) {
                                                return (
                                                    <span key={pageNumber} className="px-2 py-2 text-gray-400">
                                                        ...
                                                    </span>
                                                );
                                            }
                                            return null;
                                        })}
                                    </div>

                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className="flex items-center px-3 py-2 text-sm font-medium text-gray-300 bg-gray-700 border border-gray-600 rounded-r-md hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Siguiente
                                        <ChevronRightIcon />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}

                <div className="mt-6 text-xs text-gray-500 text-center">
                    Administrable de clientes recurrentes para Motos
                </div>
            </div>

            {cedulaARevocar && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-800 rounded-lg shadow-2xl p-6 max-w-md w-full border border-amber-600">
                        <h3 className="text-lg font-semibold text-white mb-2 flex items-center">
                            <YupanaIcon />
                            <span className="ml-2">Remover cédula {cedulaARevocar.cedula}</span>
                        </h3>
                        <p className="text-sm text-gray-400 mb-4">
                            La próxima vez que se valide esta cédula para Motos, irá directo a Yupana en vez de crédito directo. Indica el motivo.
                        </p>
                        <textarea
                            value={motivoRevocacion}
                            onChange={(e) => setMotivoRevocacion(e.target.value)}
                            rows={3}
                            className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent mb-4"
                            placeholder="Motivo de la remoción..."
                        />
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setCedulaARevocar(null)}
                                className="py-2 px-4 border border-gray-500 rounded-md text-gray-300 hover:bg-gray-600 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmarRevocacion}
                                className="py-2 px-4 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors"
                            >
                                Remover — enviar a Yupana
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
